
import { supabase } from "@/integrations/supabase/client";

// Improved admin notification function that uses admin settings
export const sendAdminNotification = async (
  type: 'signup' | 'booking' | 'review' | 'cancellation',
  details: {
    serviceName?: string;
    userName: string;
    userEmail: string;
    details?: any;
  }
) => {
  const startTime = performance.now();
  
  try {
    console.log(`Sending admin notification for ${type}`);
    
    // Get admin settings to check if notifications are enabled
    const { data: adminSettingsData } = await supabase
      .from('admin_settings')
      .select('settings')
      .eq('id', 1)
      .single();

    const adminSettings = adminSettingsData?.settings || {};
    
    // Check if notifications are enabled for this event type
    const notificationsEnabled = adminSettings?.notifications?.[`${type}s`] || false;
    const smsEnabled = adminSettings?.sms?.enabled && adminSettings?.sms?.notifications?.[`${type}s`];
    const whatsappEnabled = adminSettings?.whatsapp?.enabled;
    const pushEnabled = adminSettings?.push?.enabled && adminSettings?.push?.notifications?.[`${type}s`];
    
    if (!notificationsEnabled && !smsEnabled && !whatsappEnabled && !pushEnabled) {
      console.log(`Admin notifications disabled for ${type}`);
      return false;
    }

    // Track notification attempt
    try {
      await supabase.from('test_runs').insert({
        family: 'admin_notification_service',
        type,
        status: 'pending',
        user_email: details.userEmail,
        user_display_name: details.userName,
        data: { 
          timestamp: new Date().toISOString(),
          notifications_enabled: notificationsEnabled,
          sms_enabled: smsEnabled,
          whatsapp_enabled: whatsappEnabled,
          push_enabled: pushEnabled
        }
      });
    } catch (trackingError) {
      console.error('Error tracking admin notification attempt:', trackingError);
    }

    // Set up retry parameters
    const maxRetries = 2;
    const baseDelay = 500;
    
    // Try to send the notification
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          console.log(`Retry attempt ${attempt} for admin notification`);
          await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt - 1)));
        }
        
        // Call the edge function
        const { data: response, error } = await supabase.functions.invoke('admin-notifications', {
          method: 'POST',
          body: { 
            type, 
            details,
            settings: adminSettings 
          }
        });
        
        if (error) {
          console.error('Error invoking admin-notifications function:', error);
          continue;
        }
        
        const endTime = performance.now();
        console.log(`Admin notification sent in ${Math.round(endTime - startTime)}ms`);
        
        // Track success
        try {
          await supabase.from('test_runs').insert({
            family: 'admin_notification_service',
            type,
            status: response?.success ? 'success' : 'failed',
            user_email: details.userEmail,
            user_display_name: details.userName,
            data: {
              duration_ms: Math.round(endTime - startTime),
              timestamp: new Date().toISOString(),
              email_sent: response?.email?.sent || false,
              sms_sent: response?.sms?.sent || false,
              whatsapp_sent: response?.whatsapp?.sent || false,
              push_sent: response?.push?.sent || false,
              push_count: response?.push?.totalSent || 0
            }
          });
        } catch (trackingError) {
          console.error('Error tracking admin notification result:', trackingError);
        }
        
        return response?.success || false;
      } catch (attemptError) {
        console.error(`Error in admin notification attempt ${attempt}:`, attemptError);
        if (attempt === maxRetries) throw attemptError;
      }
    }
    
    throw new Error(`Failed after ${maxRetries} retry attempts`);
    
  } catch (error: any) {
    console.error('Error in sendAdminNotification:', error);
    
    // Track exception
    try {
      await supabase.from('test_runs').insert({
        family: 'admin_notification_service',
        type,
        status: 'exception',
        user_email: details.userEmail,
        user_display_name: details.userName,
        data: {
          error_message: error.message,
          timestamp: new Date().toISOString()
        }
      });
    } catch (trackingError) {
      console.error('Error tracking admin notification exception:', trackingError);
    }
    
    return false;
  }
};
